import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { isSceneHeadingParagraphText } from '@narrativebench/editor-core';
import { getLeafNodePlainText } from './document-plain-text';

export type TocEntryKind = 'heading' | 'scene';

export type TocEntry = {
  level: number;
  text: string;
  pos: number;
  kind: TocEntryKind;
};

export type TocParagraphEntry = {
  text: string;
  pos: number;
};

export type ExtractedTocEntries = {
  headings: TocEntry[];
  paragraphs: TocParagraphEntry[];
};

function getNodePlainText(node: ProseMirrorNode): string {
  return node.textBetween(0, node.content.size, undefined, getLeafNodePlainText);
}

function previewParagraphText(fullText: string): string {
  const words = fullText.trim().split(/\s+/);
  return words.slice(0, 6).join(' ') + (words.length > 6 ? '...' : '');
}

export function sceneTocLevelForParentHeading(parentHeadingLevel: number): number {
  if (parentHeadingLevel <= 0) {
    return 1;
  }
  return parentHeadingLevel + 1;
}

export type ExtractTocEntriesOptions = {
  includeParagraphPreviews?: boolean;
};

export function extractTocEntries(
  doc: ProseMirrorNode,
  options?: ExtractTocEntriesOptions,
): ExtractedTocEntries {
  const includeParagraphPreviews = options?.includeParagraphPreviews !== false;
  const headings: TocEntry[] = [];
  const paragraphs: TocParagraphEntry[] = [];
  let lastHeadingLevel = 0;

  doc.descendants((node, pos) => {
    if (node.type.name === 'footnotes') {
      return false;
    }
    if (node.type.name === 'heading') {
      const level =
        node.attrs && typeof node.attrs.level === 'number' ? node.attrs.level : 1;
      const text = getNodePlainText(node);
      headings.push({ level, text, pos, kind: 'heading' });
      lastHeadingLevel = level;
      return true;
    }
    if (node.type.name === 'paragraph') {
      const fullText = getNodePlainText(node);
      if (!fullText.trim()) {
        return true;
      }
      if (isSceneHeadingParagraphText(fullText)) {
        headings.push({
          level: sceneTocLevelForParentHeading(lastHeadingLevel),
          text: fullText,
          pos,
          kind: 'scene',
        });
        return true;
      }
      if (includeParagraphPreviews) {
        paragraphs.push({ text: previewParagraphText(fullText), pos });
      }
    }
    return true;
  });

  return { headings, paragraphs };
}
